import {
  DATE_PIPE_DEFAULT_OPTIONS,
  DatePipe,
  DatePipeConfig,
} from '@angular/common';
import { inject, LOCALE_ID, Pipe, PipeTransform } from '@angular/core';

export type DateRangeInput = Date | string | number;

export interface IDateRange {
  start: DateRangeInput;
  end: DateRangeInput;
}

export type DateRangeMonthFormat = 'short' | 'full';

/**
 * Formats a date range as `start - end`, only repeating the month and year
 * when they differ between the start and end dates. The day / month order
 * follows the locale.
 *
 * Day first (e.g. en-GB):
 * `5 - 9 Oct 2026`, `28 Sept - 2 Oct 2026`, `28 Dec 2026 - 4 Jan 2027`
 *
 * Month first (e.g. en-US):
 * `Oct 5 - 9, 2026`, `Sep 28 - Oct 2, 2026`, `Dec 28, 2026 - Jan 4, 2027`
 */
@Pipe({
  name: 'dateRange',
})
export class DateRangePipe implements PipeTransform {
  private readonly locale = inject(LOCALE_ID);
  private readonly dayFirst = this.isDayFirst();
  private readonly datePipe = new DatePipe(
    this.locale,
    undefined,
    inject<DatePipeConfig>(DATE_PIPE_DEFAULT_OPTIONS, { optional: true }) ??
      undefined
  );

  transform(
    range: IDateRange | null | undefined,
    monthFormat: DateRangeMonthFormat = 'short',
    timezone?: string
  ): string | null {
    if (!range?.start || !range?.end) {
      return null;
    }

    const { start, end } = range;
    const month = monthFormat === 'full' ? 'MMMM' : 'MMM';

    const { sameYear, sameMonth, sameDay } = this.compare(start, end, timezone);

    const fullPattern = this.dayFirst ? `d ${month} y` : `${month} d, y`;

    if (sameDay) {
      return this.format(end, fullPattern, timezone);
    }

    const [startPattern, endPattern] = this.dayFirst
      ? [sameMonth ? 'd' : sameYear ? `d ${month}` : fullPattern, fullPattern]
      : [
          sameYear ? `${month} d` : fullPattern,
          sameMonth ? 'd, y' : fullPattern,
        ];

    return `${this.format(start, startPattern, timezone)} - ${this.format(end, endPattern, timezone)}`;
  }

  /**
   * Whether the locale puts the day before the month, e.g. `d MMM y` (en-GB)
   * rather than `MMM d, y` (en-US).
   */
  private isDayFirst() {
    const types = new Intl.DateTimeFormat(this.locale, { dateStyle: 'medium' })
      .formatToParts(new Date(2000, 0, 1))
      .map(({ type }) => type);

    return types.indexOf('day') < types.indexOf('month');
  }

  /**
   * Compares calendar parts in the target timezone. Formatting (rather than
   * using `Date` getters) keeps this consistent with how the dates display.
   */
  private compare(
    start: DateRangeInput,
    end: DateRangeInput,
    timezone?: string
  ) {
    const [startYear, startMonth, startDay] = this.toParts(start, timezone);
    const [endYear, endMonth, endDay] = this.toParts(end, timezone);

    const sameYear = startYear === endYear;
    const sameMonth = sameYear && startMonth === endMonth;
    const sameDay = sameMonth && startDay === endDay;

    return { sameYear, sameMonth, sameDay };
  }

  private toParts(date: DateRangeInput, timezone?: string) {
    return (this.format(date, 'y-M-d', timezone) ?? '').split('-');
  }

  private format(date: DateRangeInput, pattern: string, timezone?: string) {
    return this.datePipe.transform(date, pattern, timezone);
  }
}
